import jsPDF from 'jspdf';

const brandNavy = [26, 31, 113];
const brandPink = [230, 0, 126];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Format a date value as e.g. "28 June 2026" using local time
// (mirrors the date-only handling used elsewhere in the app).
const friendlyDate = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const isSingleDay = (booking) =>
  !booking.endDate || friendlyDate(booking.startDate) === friendlyDate(booking.endDate);

// "Multi Activities 2" -> "Multi Activities" (trailing instance numbers are dropped from the letter).
const cleanProductName = (name) => (name || '').replace(/\s+\d+\s*$/, '').trim();

/**
 * Build the structured content for a student's welcome letter. Pure function shared by
 * the PDF generator and the on-screen preview so the two never drift apart.
 *
 * @param {Object}   args
 * @param {Object}   args.student   full student record (api/students/:id)
 * @param {Array}    args.stays     stays sorted by arrival date
 * @param {Array}    args.bookings  bookings sorted by start date
 * @param {dayjs|string} args.letterDate  date shown on the letter
 * @param {string}   [args.addressee]  overrides the salutation recipient
 * @returns {Object} the letter model (see fields below)
 */
export function buildLetterModel({ student = {}, stays = [], bookings = [], letterDate, addressee } = {}) {
  const formattedDate = letterDate && letterDate.format ? letterDate.format('DD MMMM YYYY') : letterDate;

  const studentFullName = `${student.studentName || ''} ${student.studentSurname || ''}`.trim();
  const firstName = student.studentName || 'your child';
  const recipient = (addressee && addressee.trim())
    ? addressee.trim()
    : `Parent / Guardian of ${studentFullName || 'our student'}`;

  // Residential products are identified by "week" in the name (e.g. "Dollar week 1").
  // They are rolled up into a single "Stay at <campus> from X to Y" line per campus.
  const isResidentialBooking = (b) => /week/i.test(b.productName || '');
  const residentialBookings = bookings.filter(isResidentialBooking);
  const activityBookings = bookings.filter((b) => !isResidentialBooking(b));

  // "Dollar week 1" -> "Dollar"; falls back to the stay campus if the name is just "Week n".
  const deriveCampus = (name) => (name || '').replace(/\s*week\s*\d*\s*$/i, '').trim();

  const stayGroups = [];
  residentialBookings.forEach((b) => {
    const campus = deriveCampus(b.productName) || (stays[0] && stays[0].campus) || '';
    let group = stayGroups.find((g) => g.campus === campus);
    if (!group) {
      group = { campus, start: b.startDate, end: b.endDate };
      stayGroups.push(group);
    } else {
      if (b.startDate && (!group.start || new Date(b.startDate) < new Date(group.start))) group.start = b.startDate;
      if (b.endDate && (!group.end || new Date(b.endDate) > new Date(group.end))) group.end = b.endDate;
    }
  });
  stayGroups.sort((a, b) => new Date(a.start) - new Date(b.start));

  const primaryCampus =
    (stayGroups[0] && stayGroups[0].campus) || (stays[0] && stays[0].campus) || 'our camp';
  const overallArrival = stayGroups.length
    ? stayGroups[0].start
    : (stays[0] && stays[0].arrivalDate) || null;
  const overallDeparture = stayGroups.length
    ? stayGroups[stayGroups.length - 1].end
    : (stays[stays.length - 1] && stays[stays.length - 1].departureDate) || null;

  let welcome = `We are delighted to welcome ${firstName} to BLISS at ${primaryCampus}. `;
  if (overallArrival && overallDeparture) {
    welcome += `Their stay with us runs from ${friendlyDate(overallArrival)} to ${friendlyDate(overallDeparture)}. `;
  }
  welcome +=
    'Everyone in our team is looking forward to making it a safe, happy and memorable experience. ' +
    'This letter summarises the key details of the booking and a few practical notes to help you prepare.';

  const stayBullets = stayGroups.map((g) => {
    const label = g.campus ? `Stay at ${g.campus}` : 'Stay';
    return g.start && g.end ? `${label} from ${friendlyDate(g.start)} to ${friendlyDate(g.end)}` : label;
  });
  const activityBullets = activityBookings.map((b) => {
    const name = cleanProductName(b.productName) || 'Activity';
    if (isSingleDay(b)) {
      const d = friendlyDate(b.startDate);
      return d ? `${name} (${d})` : name;
    }
    const start = friendlyDate(b.startDate);
    const end = friendlyDate(b.endDate);
    return start && end ? `${name} (${start} – ${end})` : name;
  });
  const programmeBullets = [...stayBullets, ...activityBullets];

  const kitList = [
    'Comfortable clothing for activities and a warm layer for the evenings',
    'Waterproof jacket and sturdy outdoor shoes',
    'Swimwear and a towel',
    'Toiletries and any personal medication (clearly labelled)',
    'Reusable water bottle and a small daypack for excursions',
    'Sun cream and a hat',
  ];
  const bookingNames = bookings.map((b) => (b.productName || '').toLowerCase());
  const hasBooking = (re) => bookingNames.some((n) => re.test(n));
  if (hasBooking(/horse\s*riding/)) {
    kitList.push('For horse riding: footwear with a small heel suitable for riding (riding boots are not provided).');
  }
  if (hasBooking(/outdoor adventure/)) {
    kitList.push('For outdoor adventure: an older pair of sports shoes (or similar) that can be worn for muddy or wet activities.');
  }
  if (student.tshirtSize) {
    kitList.push(`Note: we have ${firstName}'s t-shirt size on file as "${student.tshirtSize}".`);
  }

  const permissions = [
    ['Swimming pool', student.hasPoolPermission],
    ['Photography', student.hasPhotoPermission],
    ['Medical treatment', student.hasMedicalPermission],
    ['Hospital treatment', student.hasHospitalPermission],
    ['Excursions', student.hasExcursionPermission],
    ['Activities', student.hasActivityPermission],
    ['Supervision', student.hasSupervisionPermission],
  ];
  const granted = permissions.filter(([, v]) => v).map(([label]) => label);
  const missing = permissions.filter(([, v]) => !v).map(([label]) => label);

  return {
    title: 'Welcome Letter',
    formattedDate,
    studentFullName,
    firstName,
    recipient,
    filename: `Welcome_Letter_${studentFullName || student.id}_${formattedDate}.pdf`,
    welcome,
    programmeIntro: programmeBullets.length > 0
      ? `During the stay ${firstName} is booked onto the following:`
      : null,
    programmeBullets,
    programmeFallback: programmeBullets.length > 0
      ? null
      : `The full activity programme will be confirmed closer to the arrival date. ${firstName} will take part in a varied schedule of lessons, sports and excursions.`,
    kitIntro: 'To help your child get the most from their stay, please pack the following:',
    kitList,
    permissionsGranted: granted.length > 0 ? `We have your consent recorded for: ${granted.join(', ')}.` : null,
    permissionsMissing: missing.length > 0
      ? `We do not yet have consent recorded for: ${missing.join(', ')}. Please contact us before arrival if you would like to update these.`
      : null,
    dietaryNote: student.allergies
      ? `Our records show the following allergies / notes: ${student.allergies}. Please let us know if anything has changed.`
      : null,
    closingParagraph: `If you have any questions before arrival, please do not hesitate to get in touch. We look forward to welcoming ${firstName}.`,
    signOffLine: 'With warm regards,',
    teamLine: 'The BLISS Team',
  };
}

// Draws text where the first two letters of each word are pink and the rest navy,
// centred on xCenter. Mirrors the accent style used elsewhere in the student PDFs.
const drawBrandAccentText = (pdf, text, xCenter, y, fontSize) => {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(fontSize);

  const parts = [];
  const words = text.split(' ');
  words.forEach((word, wordIndex) => {
    if (!word) return;
    if (word.length >= 2) {
      parts.push({ text: word.slice(0, 2), color: brandPink });
      if (word.length > 2) parts.push({ text: word.slice(2), color: brandNavy });
    } else {
      parts.push({ text: word, color: brandPink });
    }
    if (wordIndex < words.length - 1) parts.push({ text: ' ', color: brandNavy });
  });

  const totalWidth = parts.reduce((sum, part) => sum + pdf.getTextWidth(part.text), 0);
  let x = xCenter - totalWidth / 2;
  parts.forEach((part) => {
    pdf.setTextColor(...part.color);
    pdf.text(part.text, x, y);
    x += pdf.getTextWidth(part.text);
  });
};

/**
 * Build a branded, parent-facing welcome letter for a student and trigger a download.
 *
 * @param {Object}   args
 * @param {Object}   args.student   full student record (api/students/:id)
 * @param {Array}    args.stays     stays sorted by arrival date
 * @param {Array}    args.bookings  bookings sorted by start date
 * @param {dayjs|string} args.letterDate  date shown on the letter
 * @param {string}   [args.addressee]  overrides the salutation recipient; defaults to "Parent / Guardian of <name>"
 * @param {Object}   [args.assets]  optional { logo, footer } image data; defaults to PUBLIC_URL paths
 */
export function generateStudentLetterPDF({ student = {}, stays = [], bookings = [], letterDate, addressee, assets } = {}) {
  const model = buildLetterModel({ student, stays, bookings, letterDate, addressee });
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 50;
  const contentWidth = pageWidth - marginX * 2;
  const footerSpace = 80; // reserved at the bottom for the footer band
  let y = 40;

  const logo = (assets && assets.logo) || process.env.PUBLIC_URL + '/assets/img/blissLogo.png';
  const footerImg = (assets && assets.footer) || process.env.PUBLIC_URL + '/assets/img/footer.png';

  const drawFooter = () => {
    pdf.setDrawColor(...brandNavy);
    pdf.setLineWidth(1);
    pdf.line(marginX, pageHeight - 70, pageWidth - marginX, pageHeight - 70);
    pdf.addImage(footerImg, 'PNG', (pageWidth - 120) / 2, pageHeight - 55, 120, 35);
  };

  // Start a new page if `needed` pts won't fit above the footer band.
  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - footerSpace) {
      drawFooter();
      pdf.addPage();
      y = 50;
    }
  };

  const writeParagraph = (text, { fontSize = 11, lineGap = 4, spaceAfter = 12, fontStyle = 'normal' } = {}) => {
    pdf.setFont('helvetica', fontStyle);
    pdf.setFontSize(fontSize);
    pdf.setTextColor(40);
    const lineHeight = fontSize + lineGap;
    pdf.splitTextToSize(text, contentWidth).forEach((line) => {
      ensureSpace(lineHeight);
      pdf.text(line, marginX, y);
      y += lineHeight;
    });
    y += spaceAfter;
  };

  const writeBullets = (items, { fontSize = 11, lineGap = 4 } = {}) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    const lineHeight = fontSize + lineGap;
    const bulletIndent = 14;
    items.forEach((item) => {
      pdf.splitTextToSize(item, contentWidth - bulletIndent).forEach((line, idx) => {
        ensureSpace(lineHeight);
        if (idx === 0) {
          pdf.setTextColor(...brandPink);
          pdf.text('•', marginX, y);
        }
        pdf.setTextColor(40);
        pdf.text(line, marginX + bulletIndent, y);
        y += lineHeight;
      });
    });
    y += 8;
  };

  const writeHeading = (text) => {
    ensureSpace(26);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12.5);
    pdf.setTextColor(...brandNavy);
    pdf.text(text, marginX, y);
    y += 18;
  };

  // ---- Header ----
  const logoWidth = 50;
  const logoHeight = 60;
  pdf.addImage(logo, 'PNG', marginX, y, logoWidth, logoHeight);
  drawBrandAccentText(pdf, model.title, pageWidth - 110, y + logoHeight / 2 + 8, 20);

  const lineY = y + logoHeight + 10;
  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(marginX, lineY, pageWidth - marginX, lineY);
  y = lineY + 26;

  // ---- Date ----
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.setTextColor(40);
  pdf.text(String(model.formattedDate || ''), pageWidth - marginX, y, { align: 'right' });
  y += 22;

  // ---- Salutation ----
  writeParagraph(`Dear ${model.recipient},`, { fontStyle: 'bold', spaceAfter: 14 });

  // ---- Welcome + stay dates ----
  writeParagraph(model.welcome);

  // ---- Activities / bookings ----
  writeHeading('Activities & programme');
  if (model.programmeBullets.length > 0) {
    writeParagraph(model.programmeIntro, { spaceAfter: 6 });
    writeBullets(model.programmeBullets);
  } else {
    writeParagraph(model.programmeFallback);
  }

  // ---- What to bring ----
  writeHeading('What to bring');
  writeParagraph(model.kitIntro, { spaceAfter: 6 });
  writeBullets(model.kitList);

  // ---- Permissions summary ----
  writeHeading('Permissions on file');
  if (model.permissionsGranted) writeParagraph(model.permissionsGranted, { spaceAfter: 6 });
  if (model.permissionsMissing) writeParagraph(model.permissionsMissing);

  // ---- Dietary & medical notes ----
  if (model.dietaryNote) {
    writeHeading('Dietary & medical notes');
    writeParagraph(model.dietaryNote);
  }

  // ---- Sign-off ----
  writeParagraph(model.closingParagraph);
  writeParagraph(model.signOffLine, { spaceAfter: 4 });
  writeParagraph(model.teamLine, { fontStyle: 'bold', spaceAfter: 0 });

  drawFooter();

  pdf.save(model.filename);
}
